/**
 * The Theme Document — the versioned, server-persisted unit everything in the
 * studio reduces to. `themes.doc` holds exactly this shape; revisions, presets,
 * marketplace listings and community snapshots are all the same document.
 *
 * The flat `StudioState` remains the editor's working view (canvas + inspector
 * bind to it); `docFromFlat` / `flattenDoc` convert at the store boundary.
 * Channels and the community's name/tagline are deliberately NOT part of the
 * doc — they're community structure/identity, persisted to `community_channels`
 * and the `communities` row, so a theme can be installed on a different
 * community without renaming it.
 */
import { getTheme } from '@/features/theme/themes';
import { migrateBannerDesign, type BannerDesign } from './bannerLayers';
import {
  defaultStudioState,
  type BackdropDim,
  type BgTint,
  type CardStyle,
  type CornerStyle,
  type Density,
  type FontChoice,
  type MessageStyle,
  type NameStyle,
  type RailWidth,
  type StudioChannel,
  type StudioState,
} from '../studioModel';

export const THEME_DOC_VERSION = 1;

export interface ThemeDocBackground {
  /** 'catalog' = bundled tN.webp skin id; 'asset' = uploaded image URL; 'none' = clean gradient. */
  kind: 'catalog' | 'asset' | 'none';
  ref: string | null;
  dim: BackdropDim;
  tint: BgTint;
  vignette: boolean;
}

export interface ThemeDoc {
  version: typeof THEME_DOC_VERSION;
  background: ThemeDocBackground;
  accent: string;
  font: FontChoice;
  surface: { material: CardStyle; corners: CornerStyle; density: Density };
  layout: { railWidth: RailWidth; messageStyle: MessageStyle };
  effects: { glow: boolean; animated: boolean };
  identity: {
    nameStyle: NameStyle;
    joinLabel: string;
    /** Hosted banner URL (storage) — never a data URL. `design` is the editable
     *  layered source (renders live on themed surfaces; flattened on publish).
     *  `focal` (0–100 percentages) reframes the banner inside each surface crop. */
    banner: { url: string | null; focal: { x: number; y: number }; design: BannerDesign | null };
  };
}

export function defaultThemeDoc(): ThemeDoc {
  return docFromFlat(defaultStudioState());
}

/** Doc → the flat editor view the canvas + inspector bind to. */
export function flattenDoc(
  doc: ThemeDoc,
  extras: { communityName: string; tagline: string; channels: StudioChannel[] },
): StudioState {
  const bg = doc.background;
  return {
    themeId: bg.kind === 'catalog' && bg.ref ? bg.ref : bg.kind === 'asset' ? 'custom' : 'framer',
    customImage: bg.kind === 'asset' ? bg.ref : null,
    dim: bg.dim,
    accent: doc.accent,
    font: doc.font,
    cardStyle: doc.surface.material,
    corners: doc.surface.corners,
    density: doc.surface.density,
    railWidth: doc.layout.railWidth,
    messageStyle: doc.layout.messageStyle,
    bgTint: bg.tint,
    vignette: bg.vignette,
    glow: doc.effects.glow,
    animated: doc.effects.animated,
    nameStyle: doc.identity.nameStyle,
    joinLabel: doc.identity.joinLabel,
    banner: doc.identity.banner.url,
    bannerFocal: doc.identity.banner.focal,
    bannerDesign: doc.identity.banner.design,
    communityName: extras.communityName,
    tagline: extras.tagline,
    channels: extras.channels,
  };
}

/** Flat editor view → doc (drops community identity + channels by design). */
export function docFromFlat(state: StudioState): ThemeDoc {
  const isCustom = state.themeId === 'custom';
  return {
    version: THEME_DOC_VERSION,
    background: {
      kind: isCustom ? (state.customImage ? 'asset' : 'none') : state.themeId === 'framer' ? 'none' : 'catalog',
      ref: isCustom ? state.customImage : state.themeId === 'framer' ? null : state.themeId,
      dim: state.dim,
      tint: state.bgTint,
      vignette: state.vignette,
    },
    accent: state.accent,
    font: state.font,
    surface: { material: state.cardStyle, corners: state.corners, density: state.density },
    layout: { railWidth: state.railWidth, messageStyle: state.messageStyle },
    effects: { glow: state.glow, animated: state.animated },
    identity: {
      nameStyle: state.nameStyle,
      joinLabel: state.joinLabel,
      banner: { url: state.banner, focal: state.bannerFocal, design: state.bannerDesign },
    },
  };
}

/**
 * Validate/upgrade a doc read from the DB (or a community snapshot). Unknown or
 * partial input degrades field-by-field to defaults — a bad doc can restyle a
 * community, never crash it. Bump THEME_DOC_VERSION + add an upgrade step here
 * when the shape evolves.
 */
export function migrateDoc(raw: unknown): ThemeDoc {
  const base = defaultThemeDoc();
  if (!raw || typeof raw !== 'object') return base;
  const d = raw as Partial<ThemeDoc> & Record<string, unknown>;
  const bg = (d.background ?? {}) as Partial<ThemeDocBackground>;
  const kind = bg.kind === 'asset' || bg.kind === 'catalog' || bg.kind === 'none' ? bg.kind : base.background.kind;
  const ref = typeof bg.ref === 'string' ? bg.ref : null;
  return {
    version: THEME_DOC_VERSION,
    background: {
      kind: kind === 'catalog' && !getTheme(ref ?? '') ? 'none' : kind,
      ref,
      dim: pick(bg.dim, ['light', 'medium', 'dark'], base.background.dim),
      tint: pick(bg.tint, ['off', 'subtle', 'bold'], base.background.tint),
      vignette: typeof bg.vignette === 'boolean' ? bg.vignette : base.background.vignette,
    },
    accent: typeof d.accent === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(d.accent) ? d.accent : base.accent,
    font: pick(d.font, ['sans', 'grotesk', 'rounded', 'serif', 'slab', 'mono', 'display'], base.font),
    surface: {
      material: pick(d.surface?.material, ['glass', 'solid', 'outline'], base.surface.material),
      corners: pick(d.surface?.corners, ['sharp', 'soft', 'round'], base.surface.corners),
      density: pick(d.surface?.density, ['tight', 'cozy', 'roomy'], base.surface.density),
    },
    layout: {
      railWidth: pick(d.layout?.railWidth, ['narrow', 'normal', 'wide'], base.layout.railWidth),
      messageStyle: pick(d.layout?.messageStyle, ['flat', 'bubble'], base.layout.messageStyle),
    },
    effects: {
      glow: typeof d.effects?.glow === 'boolean' ? d.effects.glow : base.effects.glow,
      animated: typeof d.effects?.animated === 'boolean' ? d.effects.animated : base.effects.animated,
    },
    identity: {
      nameStyle: pick(d.identity?.nameStyle, ['plain', 'gradient'], base.identity.nameStyle),
      joinLabel: typeof d.identity?.joinLabel === 'string' ? d.identity.joinLabel : base.identity.joinLabel,
      banner: {
        url: typeof d.identity?.banner?.url === 'string' && !d.identity.banner.url.startsWith('data:')
          ? d.identity.banner.url
          : null,
        focal: migrateFocal(d.identity?.banner?.focal),
        design: migrateBannerDesign((d.identity?.banner as { design?: unknown } | undefined)?.design),
      },
    },
  };
}

/** Focal is `{x,y}` percentages now; v1 docs stored a CSS position string. */
function migrateFocal(raw: unknown): { x: number; y: number } {
  if (raw && typeof raw === 'object') {
    const f = raw as { x?: unknown; y?: unknown };
    if (typeof f.x === 'number' && typeof f.y === 'number') {
      return { x: clampPct(f.x), y: clampPct(f.y) };
    }
  }
  if (typeof raw === 'string') {
    const kw = (word: string) => (word === 'left' || word === 'top' ? 0 : word === 'right' || word === 'bottom' ? 100 : 50);
    const parts = raw.trim().split(/\s+/);
    const num = (p?: string) => (p?.endsWith('%') ? clampPct(parseFloat(p)) : p ? kw(p) : 50);
    return { x: num(parts[0]), y: num(parts[1]) };
  }
  return { x: 50, y: 50 };
}

function clampPct(v: number): number {
  return Number.isFinite(v) ? Math.min(100, Math.max(0, v)) : 50;
}

/** Legacy `winr-studio-draft*` localStorage payloads (flat StudioState) → doc. */
export function migrateLegacyDraft(parsed: Partial<StudioState>): ThemeDoc {
  const base = defaultStudioState();
  // Data-URL banners/backgrounds from old drafts are dropped (they were never shareable).
  const merged: StudioState = { ...base, ...parsed, channels: base.channels };
  if (merged.banner?.startsWith('data:')) merged.banner = null;
  if (merged.customImage?.startsWith('data:')) {
    merged.customImage = null;
    if (merged.themeId === 'custom') merged.themeId = base.themeId;
  }
  return migrateDoc(docFromFlat(merged));
}

function pick<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}
